import { materialOrderNumber } from './materialOrderNumber.js';
import { materialColorNeeds } from './materialColors.js';
const round = n => Math.round(n * 10000) / 10000;
export function productionMaterialNeeds(order) {
  return (order.products || []).flatMap(product => (product.cost_lines || []).filter(l => l.line_type === 'material').flatMap(line => {
    const snapshot = (product.color_lines || []).map(c => c.material_colors?.[line.source_line_id]).find(Boolean) || {};
    const process = (product.cost_lines || []).find(p => p.line_type === 'process' && line.process_source_line_id && (p.source_line_id === line.process_source_line_id || p.id === line.process_source_line_id));
    const material = { ...line, requires_color: snapshot.requires_color ?? (snapshot.not_applicable === false || Boolean(snapshot.color)), quantity_per_unit: line.quantity_per_unit };
    return materialColorNeeds(material, product.color_lines).filter(c => c.totalQuantity > 0).map(color => {
      const reference = line.material_snapshot?.reference?.supplier_ref || snapshot.reference || String(line.material_snapshot?.material?.code || '');
      const supplier = line.material_snapshot?.supplier?.name || line.supplier_name_snapshot || snapshot.supplier || 'Sin proveedor';
      const key = JSON.stringify([line.material_snapshot?.reference?.supplier_id || line.supplier_id || supplier, reference || line.source_line_id || line.id, line.unit || '', color.color]);
      return { id: JSON.stringify([product.id, line.id, color.color]), key, productId: product.id,
        article: product.product_code_snapshot, description: product.description_snapshot,
        materialId: line.material_snapshot?.material?.id || line.material_snapshot?.reference?.material_id || line.material_id || null,
        garments: product.total_quantity, costLineId: line.id, source_line_id: line.source_line_id || line.id, color_usage: snapshot.usage || line.color_usage || '',
        process: line.planning_process_name || process?.name_snapshot || line.process_name_snapshot || 'Sin proceso',
        processId: line.planning_process_id || process?.process_id || line.process_id || line.process_name_snapshot || 'Sin proceso',
        processOrder: line.planning_process_order ?? process?.sort_order ?? line.sort_order ?? 0,
        supplierId: line.material_snapshot?.reference?.supplier_id || line.supplier_id || supplier,
        name: line.name_snapshot || snapshot.name || 'Material', supplier, reference, unit: line.unit || '', color: color.color,
        image: product.product?.images?.[product.product?.main_image_index || 0] || product.product?.images?.[0] || null,
        quantity_per_unit: Number(line.quantity_per_unit || 0), waste_pct: Number(line.waste_pct || 0), unit_cost: Number(line.unit_cost || 0),
        supplier_description: line.material_snapshot?.reference?.supplier_description || '', notes: line.notes || '',
        pending: color.pending, needed: color.totalQuantity, amount: color.totalQuantity * Number(line.unit_cost || 0) };
    });
  }));
}
// Use the current catalog preference, not the historical cost snapshot.
export function visibleMaterialNeeds(needs, hiddenMaterialIds = new Set()) {
  return needs.filter(need => !hiddenMaterialIds.has(need.materialId));
}
export function allocateMaterialOrders(needs, orders, includeReceived = true) {
  const available = new Map();
  for (const order of orders) available.set(order.material_key, round((available.get(order.material_key) || 0) + Number(order.confirmed_quantity || 0)));
  const totals = new Map(available);
  const awaiting = new Set(orders.filter(o => o.confirmed_quantity == null).map(o => o.material_key));
  const products = [...new Set(needs.map(n => n.productId))];
  const assigned = new Map();
  // Re-evaluate coverage after each article; ratios avoid comparing metres to units.
  while (products.length) {
    const coverage = id => {
      const demand = new Map();
      for (const n of needs.filter(n => n.productId === id)) demand.set(n.key, (demand.get(n.key) || 0) + n.needed);
      return [...demand].reduce((sum, [key, qty]) => sum + Math.min(1, (available.get(key) || 0) / qty), 0) / demand.size;
    };
    products.sort((a, b) => coverage(b) - coverage(a) || String(a).localeCompare(String(b)));
    const id = products.shift();
    for (const n of needs.filter(n => n.productId === id)) {
      const qty = n.pending ? 0 : round(Math.min(n.needed, available.get(n.key) || 0));
      assigned.set(n.id, qty); available.set(n.key, round((available.get(n.key) || 0) - qty));
    }
  }
  const received = includeReceived ? new Map(allocateMaterialOrders(needs, orders.map(o => ({ ...o, confirmed_quantity: o.received_quantity || 0 })), false).map(n => [n.id, n.allocated])) : new Map();
  const accepted = new Set(orders.filter(o => o.accepted_for_production && o.received_quantity > 0).map(o => o.material_key));
  return needs.map(n => {
    const allocated = assigned.get(n.id) || 0;
    return { ...n, acceptedForProduction: !n.pending && accepted.has(n.key), allocated, receivedAllocated: received.get(n.id) || 0, status: received.get(n.id) >= n.needed ? 'received' : allocated >= n.needed ? 'covered' : allocated > 0 ? 'partial' : totals.get(n.key) > 0 ? 'ordered' : awaiting.has(n.key) ? 'awaiting' : 'missing' };
  });
}
export function groupMaterialNeeds(needs) {
  const groups = new Map();
  for (const n of needs) {
    if (!groups.has(n.key)) groups.set(n.key, { ...n, needed: 0, amount: 0 });
    groups.get(n.key).amount += Number(n.amount || 0);
    groups.get(n.key).needed = round(groups.get(n.key).needed + n.needed);
  }
  return [...groups.values()].sort((a,b) => a.supplier.localeCompare(b.supplier) || a.name.localeCompare(b.name));
}
export function materialOrderText(orderNumber, line, quantity, id) {
  return `Pedido ${id}\nOrden de fabricación: ${orderNumber}\nProveedor: ${line.supplier}\n${line.name}\nReferencia: ${line.reference || 'Sin referencia'}\nColor: ${line.color}\nCantidad: ${quantity} ${line.unit}`;
}

export function materialOrderStatus(order) {
  if (order.confirmed_quantity == null) return 'Pedido';
  if (Number(order.confirmed_quantity) === 0) return 'Sin disponibilidad';
  return Number(order.received_quantity || 0) >= Number(order.confirmed_quantity) ? 'Recibido' : 'Confirmado';
}
export function materialOrderSummary(needed, orders) {
  const requested = orders.reduce((sum, o) => sum + Number(o.quantity), 0);
  const confirmed = orders.reduce((sum, o) => sum + Number(o.confirmed_quantity || 0), 0);
  const received = round(orders.reduce((sum, o) => sum + Number(o.received_quantity || 0), 0));
  const pending = orders.filter(o => o.confirmed_quantity == null);
  const awaiting = pending.reduce((sum, o) => sum + Number(o.quantity), 0);
  return { requested: round(requested), confirmed: round(confirmed), received, toReceive: round(Math.max(0, confirmed - received)), canReceive: !pending.length && confirmed > received, pending, awaiting: round(awaiting),
    uncovered: round(Math.max(0, needed - confirmed)), toOrder: round(Math.max(0, needed - confirmed - awaiting)),
    label: !orders.length ? 'Preparar pedido' : pending.length ? 'Pedido' : confirmed === 0 ? 'Sin disponibilidad' : received >= confirmed ? 'Recibido' : 'Confirmado' };
}
export function materialOrderDocumentText(order) {
  const quantity = order.confirmed_quantity == null ? '' : `\nCantidad confirmada: ${Number(order.confirmed_quantity).toLocaleString('es-ES', { maximumFractionDigits: 4 })}`;
  return `${order.content.replace(/^Pedido [^\n]+/, `Pedido ${materialOrderNumber(order)}`)}\n\nEstado: ${materialOrderStatus(order)}${quantity}${order.expected_delivery_date ? `\nEntrega prevista: ${order.expected_delivery_date}` : ''}`;
}

// Same shape as the simulator matrix; purchase keys remain supplier/reference/unit/color.
export function buildProductionNeedsMatrix(order, allocated) {
  const columns = new Map();
  const rows = (order.products || []).map(product => {
    const cells = new Map();
    for (const need of allocated.filter(n => n.productId === product.id)) {
      const key = JSON.stringify([need.processId, need.supplierId]);
      if (!columns.has(key)) columns.set(key, { key, processId: need.processId, process: need.process, supplier: need.supplier, order: need.processOrder });
      else columns.get(key).order = Math.min(columns.get(key).order, need.processOrder);
      if (!cells.has(key)) cells.set(key, []);
      const lines = cells.get(key);
      let line = lines.find(l => l.costLineId === need.costLineId && l.reference === need.reference && l.unit === need.unit);
      if (!line) { line = { ...need, colors: [], total_quantity: 0 }; lines.push(line); }
      line.colors.push({ ...need, totalQuantity: need.needed });
      line.total_quantity = round(line.total_quantity + need.needed);
    }
    const articleNeeds = allocated.filter(n => n.productId === product.id);
    const receivedCount = articleNeeds.filter(n => !n.pending && (n.receivedAllocated >= n.needed || n.acceptedForProduction)).length;
    const readiness = !articleNeeds.length ? 'empty' : receivedCount === articleNeeds.length ? 'ready' : 'pending';
    const images = product.product?.images || [];
    return { image: images[product.product?.main_image_index || 0] || images[0] || null,
      manufacturingStartConfirmed: Boolean(product.manufacturing_start_confirmed), readiness, receivedCount, materialCount: articleNeeds.length, key: product.id, code: product.product_code_snapshot, description: product.description_snapshot, garments: product.total_quantity, cells };
  });
  return { rows, columns: [...columns.values()].sort((a,b) => a.order-b.order || a.process.localeCompare(b.process, 'es') || a.supplier.localeCompare(b.supplier, 'es')) };
}
export function materialReportGroups(groups) {
  const result = new Map();
  for (const group of groups) {
    const key = JSON.stringify(JSON.parse(group.key).slice(0, -1));
    if (!result.has(key)) result.set(key, { ...group, key, colors: [] });
    result.get(key).colors.push(group);
  }
  return [...result.values()];
}

export function parseConfirmedQuantity(value) {
  const text = String(value ?? '').trim().replace(',', '.');
  return /^\d+(?:\.\d{1,4})?$/.test(text) && Number(text) < 1000000000 ? Number(text) : NaN;
}

// Compare purchase totals, never capped allocations, so surplus remains visible.
export function materialQuantityAlert(needed, orders) {
  const summary = materialOrderSummary(needed, orders);
  const hasReceipt = orders.some(o => Number(o.received_quantity) > 0);
  if (!hasReceipt && !orders.some(o => o.confirmed_quantity != null)) return null;
  const quantity = hasReceipt ? summary.received : summary.confirmed;
  const excess = quantity > round(needed * 1.2);
  const shortage = quantity < round(needed * 0.9);
  if (!excess && !shortage) return null;
  return { kind: excess ? 'excess' : 'shortage', quantity, needed,
    source: hasReceipt ? 'recibida' : 'confirmada',
    percent: needed > 0 ? Math.abs(quantity - needed) / needed * 100 : 0 };
}
